import React, { FC, ReactChild, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  withAnalyticsEvents,
  WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { Appearance } from '@atlaskit/button';
import CustomThemeButton from '@atlaskit/button/custom-theme-button';
import RecentIcon from '@atlaskit/icon/glyph/recent';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { colors } from '@atlaskit/theme';
import Tooltip from '@atlaskit/tooltip';
import { getPermalink } from '@atlassian/bitkit-diff/exports/diff-permalink';

import { useIntl } from 'src/hooks/intl';
import {
  getIsPullRequestReadyToUsePendingMerge,
  getPullRequestIsMissingCommits,
} from 'src/redux/pull-request/selectors';

import { useMergeData } from '../hooks/merge-data';

import messages from './merge-button.i18n';

type MergeButtonProps = {
  tabIndex?: number;
} & WithAnalyticsEventsProps;

/* eslint @typescript-eslint/ban-types: "warn" */
export const MergeButton: FC<MergeButtonProps> = ({
  tabIndex,
  createAnalyticsEvent,
}) => {
  const intl = useIntl();
  const isPrReadyToUsePendingMerge = useSelector(
    getIsPullRequestReadyToUsePendingMerge
  );
  const isPullRequestMissingCommits = useSelector(
    getPullRequestIsMissingCommits
  );
  const {
    canCreatePendingMerge,
    isMergePending,
    isAsyncMergeInProgress,
    mergeChecksLoading,
    mergeChecksIsMergeable,
    customMergeChecksError,
    onClick,
  } = useMergeData();

  const isLoading =
    (isAsyncMergeInProgress || mergeChecksLoading) && !customMergeChecksError;

  useEffect(() => {
    const permalink = getPermalink();
    if (
      !permalink &&
      createAnalyticsEvent &&
      (isPrReadyToUsePendingMerge || canCreatePendingMerge)
    ) {
      // this event is only used for triggering the "pending-merges-banner" spotlight
      // and should be removed once we remove the spotlight.
      const analyticsEvent = createAnalyticsEvent({
        action: 'viewed',
        actionSubject: 'pullRequestMergeButton',
        source: 'pullRequestScreen',
        attributes: {
          pendingMerge: true,
        },
      });
      analyticsEvent.fire();
    }
  }, [createAnalyticsEvent, isPrReadyToUsePendingMerge, canCreatePendingMerge]);

  const renderButton = (iconBefore?: ReactChild) => {
    let appearance: Appearance;
    if (isLoading) {
      appearance = 'default';
    } else if (mergeChecksIsMergeable) {
      appearance = 'primary';
    } else {
      appearance = 'warning';
    }

    return (
      <CustomThemeButton
        testId={`mergeButton-${appearance}`}
        onClick={onClick}
        iconBefore={iconBefore}
        isLoading={isLoading}
        tabIndex={tabIndex}
        appearance={appearance}
        isDisabled={isPullRequestMissingCommits}
      >
        <FormattedMessage {...messages.mergePullRequestAction} />
      </CustomThemeButton>
    );
  };

  if (isMergePending) {
    const icon = (
      <RecentIcon
        size="small"
        label={intl.formatMessage(messages.pendingMergeIconLabel)}
        testId="merge-is-pending"
      />
    );
    return (
      <Tooltip
        position="top"
        content={intl.formatMessage(messages.pendingMergeInfo)}
      >
        {renderButton(icon)}
      </Tooltip>
    );
  }

  if (isPullRequestMissingCommits) {
    return (
      <Tooltip
        position="top"
        content={intl.formatMessage(messages.disabledMergeInfo)}
      >
        {renderButton()}
      </Tooltip>
    );
  }

  const icon = (
    <WarningIcon
      label={intl.formatMessage(messages.pendingMergeIconLabel)}
      testId="merge-is-not-ready"
      // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
      secondaryColor={colors.N0}
    />
  );

  return renderButton(!mergeChecksIsMergeable ? icon : undefined);
};

export default React.memo(
  withAnalyticsEvents()(
    MergeButton as React.FunctionComponent<MergeButtonProps>
  )
);
